/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { IVSphereZone } from 'generated-types';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

// vSphere Zones API URL
const VSPHERE_ZONES_API = '/api/vcenter/zones';

/**
 * Type of response returned by vSphere zones API.
 */
interface IVSphereZones {
    vcenter_uuid: string;
    zone_names: string[];
}

/**
 * @description
 *
 *   AZ vSphere Zone Message Item.
 *
 * @author Vasundhara Nalkund, Sarthak Kapoor
 */
export class AZVSphereZoneConfigItem extends withFullModalMixin(MessageItem)<IVSphereZone> {
    protected static ngDependencies = [HTTP_WRAPPER_TOKEN];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'VSphereZone',
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();
    }

    /**
     * Return vsphere zone names.
     */
    private static getVspherZoneNames(vsphereZones: IVSphereZones[]): string[] {
        const zoneNames = vsphereZones.reduce((accumulator, vsphereZone) => {
            if (Array.isArray(vsphereZone.zone_names) && vsphereZone.zone_names.length) {
                accumulator.push(...vsphereZone.zone_names);
            }

            return accumulator;
        }, []);

        return zoneNames;
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AzVsphereZoneModalComponent,
        } = await import(
            /* webpackChunkName: "availability-zone-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/availability-zone-modal/az-vsphere-zone-modal/az-vsphere-zone-modal.component'
        );

        return AzVsphereZoneModalComponent as Type<Component>;
    }

    /**
     * Fetch vSphere zones.
     */
    public async fetchVsphereZones(
        cloudId: string,
        vcenterId?: string,
    ): Promise<string[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: VSPHERE_ZONES_API,
                method: HttpMethod.POST,
                data: {
                    cloud_uuid: cloudId,
                    vcenter_uuid: vcenterId,
                },
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { vsphere_zones: vspherZones = [] } = resource;

            return vspherZones.length ? AZVSphereZoneConfigItem.getVspherZoneNames(vspherZones) : [];
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.busy = false;
        }
    }

    /**
     * Remove the selected vspher zone.
     */
    public removeVsphereZone(): void {
        delete this.config.zone_name;
    }
}
